import React, { memo, useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiEdit3, FiLock, FiRefreshCw, FiSave, FiX, FiPlus, FiTrash2 } from "react-icons/fi";
import { supabase } from "../../config/supabase";
import Toast from "../common/Toast";
import DropdownSection from "../common/DropdownSection";
import TextTooltip from "../common/TextTooltip";
import { getFinalPricingsForJob } from "../../lib/supabase/final-pricings";
import { getSentQuotationDepartments } from "../../lib/supabase/quotations";
import { computeEstimationTotals, computeEffectivePricing, isEstimatorPricingComplete } from "../../lib/supabase/finalPricingModes";
import { notifyPricingEntered } from "../../lib/supabase/pricingNotifications";
import FinalPricingDetails from "../pricing/FinalPricingDetails";

const FIELD_GROUPS = {
    beams: [
        { key: "beams", label: "Beams" },
    ],
    flooring_sheets: [
        { key: "flooring_sheets", label: "Flooring Sheets" },
    ],
    roof: [
        { key: "roof", label: "Roof Trusses" },
        { key: "labour", label: "Labour" },
    ],
    wall: [
        { key: "gf_wall", label: "Ground Floor Walls" },
        { key: "first_floor_wall", label: "1st Floor Walls" },
        { key: "second_floor_wall", label: "2nd Floor Walls" },
    ],
    floor: [
        { key: "first_floor", label: "1st Floor Trusses" },
        { key: "second_floor", label: "2nd Floor Trusses" },
    ],
};

const PRICING_SECTION_ORDER = [
    "roof",
    "wall",
    "floor",
    "beams",
    "flooring_sheets",
];

const PRICING_SECTION_LABELS = {
    beams: {
        title: "Beams",
        subtitle: "Beam pricing entered for this unit.",
        badge: "Structure",
    },
    flooring_sheets: {
        title: "Flooring Sheets",
        subtitle: "Flooring sheet pricing entered for this unit.",
        badge: "Sheets",
    },
    roof: {
        title: "Roof",
        subtitle: "Roof trusses and labour pricing.",
        badge: "Trusses",
    },
    wall: {
        title: "Wall",
        subtitle: "Wall frame pricing by level.",
        badge: "Frames",
    },
    floor: {
        title: "Floor",
        subtitle: "Floor truss pricing by level.",
        badge: "Trusses",
    },
};

const formatPricingSectionName = (type) => {
    return String(type || "Other")
        .split("_")
        .join(" ")
        .replace(/\b\w/g, char => char.toUpperCase());
};

const getPricingSectionTitle = (type) => {
    return PRICING_SECTION_LABELS[type]?.title || formatPricingSectionName(type);
};

const getPricingSectionSubtitle = (type) => {
    return PRICING_SECTION_LABELS[type]?.subtitle || "Additional pricing entered for this unit.";
};

const getPricingSectionBadge = (type) => {
    return PRICING_SECTION_LABELS[type]?.badge || "Other";
};

const sortPricingSections = (types = []) => {
    return [...types].sort((a, b) => {
        const aIndex = PRICING_SECTION_ORDER.indexOf(a);
        const bIndex = PRICING_SECTION_ORDER.indexOf(b);

        const aRank = aIndex === -1 ? 999 : aIndex;
        const bRank = bIndex === -1 ? 999 : bIndex;

        if (aRank !== bRank) return aRank - bRank;

        return getPricingSectionTitle(a).localeCompare(getPricingSectionTitle(b));
    });
};

const BASE_PRICING_FIELD_KEYS = Object.values(FIELD_GROUPS)
    .flat()
    .map(field => field.key);

const isCustomPricingField = (fieldKey) => {
    return !BASE_PRICING_FIELD_KEYS.includes(fieldKey);
};

const makeCustomFieldKey = () => {
    return `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
};


const formatMoney = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return "—";

    return `$${new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(numberValue)}`;
};

const formatLogCurrency = (value) => {
    if (value === null || value === undefined || value === '') return null;

    return `$${new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value))}`;
};

const parseMoney = (value) => {
    const cleaned = String(value || "")
        .replace(/[^\d.,]/g, "")
        .replace(/\./g, "")
        .replace(",", ".");

    if (!cleaned) return null;

    const numberValue = Number(cleaned);
    return Number.isNaN(numberValue) ? null : numberValue;
};

const getPricingDiff = (estimatorValue, departmentValue) => {
    const est = Number(estimatorValue);
    const dep = Number(departmentValue);

    if (!Number.isFinite(est) || !Number.isFinite(dep) || est <= 0) return null;

    const diff = est - dep;
    const percent = (diff / est) * 100;

    return {
        diff,
        percent,
        isProfit: diff >= 0,
    };
};

const MoneyInput = memo(({ value, onChange }) => {
    const [localValue, setLocalValue] = useState(value ? formatMoney(value) : "");

    useEffect(() => {
        setLocalValue(value ? formatMoney(value) : "");
    }, [value]);

    return (
        <input
            type="text"
            inputMode="decimal"
            value={localValue}
            onFocus={() => setLocalValue(value || "")}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => onChange(parseMoney(localValue))}
            placeholder="$0,00"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-900 focus:border-zinc-900"
        />
    );
});

const PricingReviewDropdown = ({ assignment, company, userProfile, onSaved, companySlug }) => {
    const isAdmin = ["admin", "super_admin"].includes(userProfile?.role);
    const canEditAllPricing = ["admin", "super_admin", "estimator"].includes(userProfile?.role);
    const canEditDesignerPricing = ["admin", "super_admin", "designer"].includes(userProfile?.role);
    const userDepartment = String(userProfile?.department || "").toLowerCase();
    const jobIncludesEstimation = String(assignment?.job_type || "").includes("Estimation");

    const pricingSettings = company?.settings?.pricing_settings || {};
    const enabledPricingFields = pricingSettings.enabled_pricing_fields || Object.values(FIELD_GROUPS).flat().map(f => f.key);

    const isPricingFieldEnabled = (fieldKey) => {
        return enabledPricingFields.includes(fieldKey);
    };

    const getEnabledFieldsForDepartment = (department) => {
        return (FIELD_GROUPS[department] || []).filter(field =>
            isPricingFieldEnabled(field.key)
        );
    };

    const allEnabledPricingFields = Object.values(FIELD_GROUPS)
        .flat()
        .filter(field => isPricingFieldEnabled(field.key));

    const allowedQuoteTypes = useMemo(() => {
        const required = [...new Set(
            (assignment?.job_quotes || [])
                .filter(q =>
                    q.is_required &&
                    FIELD_GROUPS[q.quote_type] &&
                    getEnabledFieldsForDepartment(q.quote_type).length > 0
                )
                .map(q => q.quote_type)
        )];

        const sortedRequired = sortPricingSections(required);

        if (isAdmin || !userDepartment) return sortedRequired;

        return sortedRequired.filter(type => type === userDepartment);
    }, [assignment?.job_quotes, userDepartment, isAdmin]);

    const estimatorPricingFields = useMemo(() => {
        return allowedQuoteTypes.flatMap(quoteType =>
            getEnabledFieldsForDepartment(quoteType)
        );
    }, [allowedQuoteTypes, enabledPricingFields]);

    const [openUnits, setOpenUnits] = useState({});
    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);
    const [editingKey, setEditingKey] = useState(null);
    const [draftValue, setDraftValue] = useState(null);
    const [savingKey, setSavingKey] = useState(null);

    const [manualUnitNumbers, setManualUnitNumbers] = useState([]);
    const [removedUnitNumbers, setRemovedUnitNumbers] = useState([]);

    const [customFieldModal, setCustomFieldModal] = useState({
        open: false,
        unitNumber: null,
        department: "",
        label: "",
    });

    const estimatorEntries = useMemo(
        () => entries.filter(e => e.type === "estimation"),
        [entries]
    );

    const activeRevision = useMemo(() => {
        const revisions = entries
            .filter(e => !e.is_archived)
            .map(e => Number(e.quote_revision || 1));

        return revisions.length ? Math.max(...revisions) : 1;
    }, [entries]);

    const archivedRevisions = useMemo(() => {
        const grouped = entries
            .filter(e => e.is_archived)
            .reduce((acc, entry) => {
                const revision = Number(entry.quote_revision || 1);
                if (!acc[revision]) acc[revision] = [];
                acc[revision].push(entry);
                return acc;
            }, {});

        return Object.entries(grouped)
            .map(([revision, rows]) => ({
                revision: Number(revision),
                rows,
            }))
            .sort((a, b) => b.revision - a.revision);
    }, [entries]);

    const hasEstimatorPricing = useMemo(
        () => estimatorEntries.some(entry =>
            allEnabledPricingFields.some(field => entry?.[field.key] != null)
        ),
        [estimatorEntries]
    );

    const unitNumbers = useMemo(() => {
        const numsFromEntries = entries
            .filter(e =>
                !e.is_archived &&
                Number(e.quote_revision || 1) === activeRevision
            )
            .map(e => Number(e.unit_number))
            .filter(Boolean);

        const savedOrManual = [...new Set([
            ...numsFromEntries,
            ...manualUnitNumbers,
        ])].sort((a, b) => a - b);

        if (savedOrManual.length > 0) {
            return savedOrManual;
        }

        return [1];
    }, [entries, activeRevision, manualUnitNumbers]);

    const loadPricing = async ({ silent = false } = {}) => {
        if (!assignment?.id || !company?.id) return;

        try {
            if (!silent) {
                setLoading(true);
            }

            const { data, error } = await supabase
                .from("job_pricing_entries")
                .select("*")
                .eq("company_id", company.id)
                .eq("job_id", assignment.id)
                .in("type", ["estimation", "department"])
                .order("quote_revision", { ascending: false })
                .order("unit_number", { ascending: true })
                .order("created_at", { ascending: true });

            if (error) throw error;

            setEntries(data || []);
        } catch (error) {
            console.error("Failed to load pricing entries:", error);
            Toast.error("Failed to load pricing entries");
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadPricing();
    }, [assignment?.id, company?.id]);

    useEffect(() => {
        if (!assignment?.id || !company?.id) return;

        const channel = supabase
            .channel(`pricing-review-${assignment.id}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "job_pricing_entries",
                    filter: `job_id=eq.${assignment.id}`,
                },
                (payload) => {
                    const row = payload.new || payload.old;
                    if (row?.company_id && row.company_id !== company.id) return;

                    patchPricingEntry(payload);
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [assignment?.id, company?.id]);

    const [finalPricings, setFinalPricings] = useState(null);
    const [sentDepartments, setSentDepartments] = useState(new Set());
    const [showFinalPricingModal, setShowFinalPricingModal] = useState(false);

    useEffect(() => {
        let cancelled = false;

        if (!assignment?.id || !company?.id) return undefined;

        Promise.all([
            getFinalPricingsForJob(company.id, assignment.id),
            getSentQuotationDepartments(company.id, assignment.id),
        ])
            .then(([pricings, sent]) => {
                if (cancelled) return;
                setFinalPricings(pricings);
                setSentDepartments(sent);
            })
            .catch(error => {
                console.error("Failed to load final pricing status:", error);
                Toast.error("Failed to load final pricing status");
            });

        return () => {
            cancelled = true;
        };
    }, [assignment?.id, company?.id]);

    // `entries` already holds every non-archived job_pricing_entries row for
    // this job (both estimator and department rows) from loadPricing above —
    // computeEstimationTotals is the same shared helper FinalPricingDetails.js
    // / FinalPricings.js / FinalPricingCalendar.js already use on that same
    // unfiltered shape, so this reuses the existing fetch instead of adding a
    // second, competing job_pricing_entries query.
    const estimationTotals = useMemo(
        () => computeEstimationTotals(entries),
        [entries]
    );

    const patchPricingEntry = (payload) => {
        const row = payload.new || payload.old;
        if (!row?.id) return;

        setEntries(prev => {
            if (payload.eventType === "DELETE") {
                return prev.filter(entry => entry.id !== row.id);
            }

            const exists = prev.some(entry => entry.id === row.id);

            if (exists) {
                return prev.map(entry =>
                    entry.id === row.id
                        ? { ...entry, ...payload.new }
                        : entry
                );
            }

            return [...prev, payload.new].sort((a, b) => {
                const unitDiff = Number(a.unit_number || 0) - Number(b.unit_number || 0);
                if (unitDiff !== 0) return unitDiff;

                return new Date(a.created_at || 0) - new Date(b.created_at || 0);
            });
        });
    };

    const getEntry = ({ type, department = null, unitNumber }) => {
        return entries.find(e =>
            !e.is_archived &&
            Number(e.quote_revision || 1) === activeRevision &&
            e.type === type &&
            (department ? e.department === department : !e.department) &&
            Number(e.unit_number) === Number(unitNumber)
        );
    };

    const getArchivedEntry = ({ rows, type, department = null, unitNumber }) => {
        return rows.find(e =>
            e.type === type &&
            (department ? e.department === department : !e.department) &&
            Number(e.unit_number) === Number(unitNumber)
        );
    };

    const getEditorKey = ({ type, department, unitNumber, fieldKey }) =>
        `${type}-${department || "estimation"}-${unitNumber}-${fieldKey}`;

    const getFieldLabel = ({ fieldKey, department = null, unitNumber = null }) => {
        const defaultField = Object.values(FIELD_GROUPS)
            .flat()
            .find(field => field.key === fieldKey);

        if (defaultField) return defaultField.label;

        const matchingEntry = entries.find(entry =>
            !entry.is_archived &&
            Number(entry.quote_revision || 1) === activeRevision &&
            (!unitNumber || Number(entry.unit_number) === Number(unitNumber)) &&
            (!department || entry.department === department) &&
            entry.custom_price_labels?.[fieldKey]
        );

        return matchingEntry?.custom_price_labels?.[fieldKey] || fieldKey;
    };

    const logPricingChange = async ({ type, department, unitNumber, fieldKey, oldValue, newValue }) => {
        const fieldLabel = getFieldLabel({ fieldKey, department, unitNumber });

        await supabase.from("job_action_logs").insert({
            job_id: assignment.id,
            company_id: company.id,
            user_id: userProfile.id,
            user_name: userProfile.full_name || userProfile.username,
            user_role: userProfile.role,
            action: "pricing_value_updated",
            details: `${type === "estimation" ? "Estimator" : department} pricing updated: ${fieldLabel} for Unit ${unitNumber} / Revision ${activeRevision}`,
            quote_type: department,
            changed_fields: [fieldLabel],
            old_values: {
                [fieldLabel]: formatLogCurrency(oldValue),
                quote_revision: activeRevision,
            },
            new_values: {
                [fieldLabel]: formatLogCurrency(newValue),
                quote_revision: activeRevision,
            },
            action_origin: "pricing_review_dropdown",
            timestamp: new Date().toISOString(),
        });
    };

    const logPricingRevisionCreated = async ({ oldRevision, newRevision, archivedCount }) => {
        await supabase.from("job_action_logs").insert({
            job_id: assignment.id,
            company_id: company.id,
            user_id: userProfile.id,
            user_name: userProfile.full_name || userProfile.username,
            user_role: userProfile.role,
            action: "pricing_revision_created",
            details: `Pricing Revision ${oldRevision} archived and Revision ${newRevision} created`,
            changed_fields: ["quote_revision", "is_archived"],
            old_values: {
                quote_revision: oldRevision,
                is_archived: false,
                archived_rows: archivedCount,
            },
            new_values: {
                quote_revision: newRevision,
                is_archived: false,
            },
            action_origin: "pricing_review_dropdown",
            timestamp: new Date().toISOString(),
        });
    };

    const logCustomPricingInputCreated = async ({ department, unitNumber, fieldKey, label }) => {
        await supabase.from("job_action_logs").insert({
            job_id: assignment.id,
            company_id: company.id,
            user_id: userProfile.id,
            user_name: userProfile.full_name || userProfile.username,
            user_role: userProfile.role,
            action: "custom_pricing_input_created",
            details: `Custom pricing input "${label}" created for ${department} / Unit ${unitNumber} / Revision ${activeRevision}`,
            quote_type: department,
            changed_fields: [fieldKey],
            old_values: null,
            new_values: {
                field_key: fieldKey,
                label,
                department,
                unit_number: unitNumber,
                quote_revision: activeRevision,
            },
            action_origin: "pricing_review_dropdown",
            timestamp: new Date().toISOString(),
        });
    };

    const logCustomPricingInputDeleted = async ({ department, unitNumber, fieldKey, label }) => {
        await supabase.from("job_action_logs").insert({
            job_id: assignment.id,
            company_id: company.id,
            user_id: userProfile.id,
            user_name: userProfile.full_name || userProfile.username,
            user_role: userProfile.role,
            action: "custom_pricing_input_deleted",
            details: `Custom pricing input "${label}" deleted from ${department} / Unit ${unitNumber} / Revision ${activeRevision}`,
            quote_type: department,
            changed_fields: [fieldKey],
            old_values: {
                field_key: fieldKey,
                label,
                department,
                unit_number: unitNumber,
                quote_revision: activeRevision,
            },
            new_values: null,
            action_origin: "pricing_review_dropdown",
            timestamp: new Date().toISOString(),
        });
    };

    const getDepartmentEntryForEstimatorField = ({ fieldKey, unitNumber, department = null }) => {
        const matchedDepartment =
            department ||
            Object.entries(FIELD_GROUPS).find(([, fields]) =>
                fields.some(field => field.key === fieldKey)
            )?.[0];

        if (!matchedDepartment) return null;

        return getEntry({
            type: "department",
            department: matchedDepartment,
            unitNumber,
        });
    };

    const hasDesignerPriceForEstimatorField = ({ unitNumber, fieldKey, department = null }) => {
        const departmentEntry = getDepartmentEntryForEstimatorField({
            fieldKey,
            unitNumber,
            department,
        });

        return getFieldValue(departmentEntry, fieldKey) != null;
    };

    const getFieldValue = (entry, fieldKey) => {
        if (!entry) return null;

        if (isCustomPricingField(fieldKey)) {
            return entry.custom_prices?.[fieldKey] ?? null;
        }

        return entry?.[fieldKey] ?? null;
    };

    const getCustomFieldsForDepartment = (unitNumber, department) => {
        const relatedEntries = entries.filter(entry =>
            !entry.is_archived &&
            Number(entry.quote_revision || 1) === activeRevision &&
            Number(entry.unit_number) === Number(unitNumber) &&
            entry.type === "department" &&
            entry.department === department
        );

        const labels = {};

        relatedEntries.forEach(entry => {
            Object.entries(entry.custom_price_labels || {}).forEach(([key, label]) => {
                labels[key] = label;
            });
        });

        return Object.entries(labels).map(([key, label]) => ({
            key,
            label,
            is_custom: true,
            department,
        }));
    };

    const getFieldsForDepartmentAndUnit = (department, unitNumber) => {
        return [
            ...getEnabledFieldsForDepartment(department),
            ...getCustomFieldsForDepartment(unitNumber, department),
        ];
    };

    const getEstimatorFieldsForUnit = (unitNumber) => {
        return allowedQuoteTypes.flatMap(quoteType =>
            getFieldsForDepartmentAndUnit(quoteType, unitNumber).map(field => ({
                ...field,
                department: field.is_custom ? quoteType : null,
            }))
        );
    };

    const getUnitHeaderPriceItems = (unitNumber) => {
        const items = [];

        const addItem = ({ entry, field, department = null, labelPrefix = "" }) => {
            const value = getFieldValue(entry, field.key);

            if (value == null) return;

            const label = getFieldLabel({
                fieldKey: field.key,
                department,
                unitNumber,
            });

            items.push({
                key: `${entry.type}-${department || entry.department || "estimator"}-${field.key}`,
                label: labelPrefix ? `${labelPrefix} ${label}` : label,
                value,
            });
        };

        // 1. Estimator pricing, in the same order as the Estimator Pricing form
        allowedQuoteTypes.forEach(quoteType => {
            const fields = getFieldsForDepartmentAndUnit(quoteType, unitNumber);

            fields.forEach(field => {
                const estimatorEntry = field.is_custom
                    ? (
                        getEntry({
                            type: "estimation",
                            department: quoteType,
                            unitNumber,
                        }) ||
                        getEntry({
                            type: "estimation",
                            unitNumber,
                        })
                    )
                    : getEntry({
                        type: "estimation",
                        unitNumber,
                    });

                if (!estimatorEntry) return;

                addItem({
                    entry: estimatorEntry,
                    field,
                    department: field.is_custom ? quoteType : null,
                    labelPrefix: "Est.",
                });
            });
        });

        // 2. Department/designer pricing, in the same order as the visible pricing sections
        allowedQuoteTypes.forEach(quoteType => {
            const departmentEntry = getEntry({
                type: "department",
                department: quoteType,
                unitNumber,
            });

            if (!departmentEntry) return;

            getFieldsForDepartmentAndUnit(quoteType, unitNumber).forEach(field => {
                addItem({
                    entry: departmentEntry,
                    field,
                    department: quoteType,
                });
            });
        });

        return items;
    };

    // labour is tracked separately from roof trusses on purpose (companies
    // want to see labour cost on its own) — it must be excluded from every
    // grand total shown in this dropdown ("Estimator Pricing" card's overall
    // total and the "Roof Pricing" card's header total), same rule as
    // DEPARTMENT_FIELD_KEYS.roof in finalPricingModes.js (which applies it to
    // every other roof total in the app: Final Pricing, Quotation, Pricing
    // Review status strip). "labour" is a fixed field key that only ever
    // belongs to roof, so no department scoping is needed here. The labour
    // field itself stays fully visible/editable in both cards — only these
    // header sums exclude it.
    const TOTALS_EXCLUDED_FIELD_KEYS = new Set(["labour"]);

    // Sum of every estimator field entered for this unit, across all
    // sections in the Estimator Pricing card — used for that card's header
    // total. Returns null (rendered as "Not entered") when nothing's been
    // entered yet, rather than a misleading $0,00.
    const getEstimatorSectionTotal = (unitNumber) => {
        let total = 0;
        let hasAny = false;

        getEstimatorFieldsForUnit(unitNumber).forEach(field => {
            if (TOTALS_EXCLUDED_FIELD_KEYS.has(field.key)) return;

            const entry = field.department
                ? (
                    getEntry({ type: "estimation", department: field.department, unitNumber }) ||
                    getEntry({ type: "estimation", unitNumber })
                )
                : getEntry({ type: "estimation", unitNumber });

            const value = getFieldValue(entry, field.key);
            if (value != null) {
                hasAny = true;
                total += Number(value);
            }
        });

        return hasAny ? total : null;
    };

    // Sum of every field entered for one department's card, this unit only —
    // the "grand total" shown next to e.g. "Roof Pricing". Null when nothing
    // in this card has been entered yet.
    const getDepartmentSectionTotal = (department, unitNumber) => {
        const entry = getEntry({ type: "department", department, unitNumber });
        let total = 0;
        let hasAny = false;

        getFieldsForDepartmentAndUnit(department, unitNumber).forEach(field => {
            if (TOTALS_EXCLUDED_FIELD_KEYS.has(field.key)) return;

            const value = getFieldValue(entry, field.key);
            if (value != null) {
                hasAny = true;
                total += Number(value);
            }
        });

        return hasAny ? total : null;
    };

    // The manually-entered final pricing record for a department (job-wide —
    // job_final_pricings has no per-unit concept), or null if none has been
    // saved yet.
    const getFinalPriceForDepartment = (department) => {
        const raw = finalPricings?.[department];
        if (raw === null || raw === undefined || raw === "") return null;

        const value = Number(raw);
        return Number.isFinite(value) ? value : null;
    };

    // Compares this unit's entered department total against the department's
    // final pricing record — replaces the old per-field "estimator vs
    // designer" diff, since final pricing is what actually matters once it
    // exists. Null until both a final price is set and something's entered.
    const getDepartmentSectionComparison = (department, unitNumber) => {
        const finalPrice = getFinalPriceForDepartment(department);
        const total = getDepartmentSectionTotal(department, unitNumber);

        if (finalPrice == null || total == null) return null;

        return getPricingDiff(finalPrice, total);
    };

    const saveSingleValue = async ({ type, department = null, unitNumber, fieldKey, value }) => {
        const key = getEditorKey({ type, department, unitNumber, fieldKey });
        const existing = getEntry({ type, department, unitNumber });

        if (type === "estimation" && !canEditAllPricing) {
            Toast.error("Only admin, super admin, or estimator can edit estimator pricing.");
            return;
        }

        if (type === "estimation" && hasDesignerPriceForEstimatorField({ unitNumber, fieldKey, department })) {
            if (!isAdmin) {
                Toast.error("Designer pricing already exists for this field. Estimator pricing is locked.");
                return;
            }

            const confirmed = window.confirm(
                "Designer pricing already exists for this field. Changing estimator pricing may affect margin/profit comparison. Continue?"
            );

            if (!confirmed) return;
        }

        if (type === "department" && !canEditDesignerPricing) {
            Toast.error("Only admin, super admin, or designer can edit designer pricing.");
            return;
        }

        if (type === "department" && existing?.[fieldKey] != null && !canEditAllPricing) {
            Toast.error("This price was already entered and is locked.");
            return;
        }

        const userName = userProfile.full_name || userProfile.username;

        const nextFieldMeta = {
            ...(existing?.field_meta || {}),
            [fieldKey]: {
                updated_by: userProfile.id,
                updated_by_name: userName,
                updated_at: new Date().toISOString(),
                value,
            },
        };

        const nextCustomPrices = isCustomPricingField(fieldKey)
            ? {
                ...(existing?.custom_prices || {}),
                [fieldKey]: value,
            }
            : (existing?.custom_prices || {});

        const nextCustomLabels = existing?.custom_price_labels || {};

        const row = {
            company_id: company.id,
            job_id: assignment.id,
            child_id: null,
            type,
            department,
            unit_number: unitNumber,

            roof: existing?.roof ?? null,
            labour: existing?.labour ?? null,
            first_floor: existing?.first_floor ?? null,
            second_floor: existing?.second_floor ?? null,
            gf_wall: existing?.gf_wall ?? null,
            first_floor_wall: existing?.first_floor_wall ?? null,
            second_floor_wall: existing?.second_floor_wall ?? null,

            beams: existing?.beams ?? null,
            flooring_sheets: existing?.flooring_sheets ?? null,

            created_by: existing?.created_by || userProfile.id,
            created_by_name: existing?.created_by_name || userProfile.full_name || userProfile.username,
            updated_by: userProfile.id,
            updated_by_name: userProfile.full_name || userProfile.username,
            field_meta: nextFieldMeta,
            quote_revision: activeRevision,
            is_archived: false,

            custom_prices: nextCustomPrices,
            custom_price_labels: nextCustomLabels,
        };

        if (isCustomPricingField(fieldKey)) {
            row.custom_prices = nextCustomPrices;
        } else {
            row[fieldKey] = value;
        }

        try {
            setSavingKey(key);

            const { error } = await supabase
                .from("job_pricing_entries")
                .upsert(row, {
                    onConflict: "job_id,type,unit_number,child_id_key,department_key,quote_revision",
                });

            if (error) throw error;

            await logPricingChange({
                type,
                department,
                unitNumber,
                fieldKey,
                oldValue: getFieldValue(existing, fieldKey),
                newValue: value,
            });

            // Estimator fields are saved one at a time here (unlike
            // SingleAssignment.js's batch "Estimator Pricing" form), so
            // there's no local "was it complete before this edit" baseline
            // to compare against — instead this just checks completeness
            // fresh after every estimation-type save and calls
            // notifyPricingEntered whenever it's true. notifyPricingEntered
            // is the one place that's actually safe to spam like this: it
            // only ever sends once per job (alreadyNotifiedPricingEntered
            // checks the notifications table itself), so re-entering,
            // editing, or deleting-and-redoing a value never produces a
            // second email — the DB check is what guarantees that, not
            // anything computed here.
            if (type === "estimation") {
                const nextEntries = existing
                    ? entries.map(entry => (entry.id === existing.id ? { ...entry, ...row } : entry))
                    : [...entries, row];

                if (isEstimatorPricingComplete({ pricingEntries: nextEntries, jobQuotes: assignment?.job_quotes })) {
                    const notifyUserIds = company?.settings?.pricing_settings?.estimation_complete_email_user_ids || [];

                    notifyPricingEntered({
                        job: assignment,
                        company,
                        companySlug,
                        userIds: notifyUserIds,
                        pricingEntries: nextEntries,
                        quoteRevision: activeRevision,
                    }).catch(err => console.error("Failed to send pricing entered notification:", err));
                }
            }

            Toast.success("Pricing saved");
            setEditingKey(null);
            setDraftValue(null);
            await loadPricing({ silent: true });
            onSaved?.();
        } catch (error) {
            console.error("Failed to save pricing:", error);
            Toast.error(error.message || "Failed to save pricing");
        } finally {
            setSavingKey(null);
        }
    };

    const canDeleteCustomField = ({ unitNumber, department, fieldKey }) => {
        if (!isCustomPricingField(fieldKey)) return false;

        const estimatorEntry = getEntry({
            type: "estimation",
            department,
            unitNumber,
        });

        const departmentEntry = getEntry({
            type: "department",
            department,
            unitNumber,
        });

        const estimatorValue = getFieldValue(estimatorEntry, fieldKey);
        const departmentValue = getFieldValue(departmentEntry, fieldKey);

        return estimatorValue == null && departmentValue == null;
    };

    const removeKeyFromObject = (obj, keyToRemove) => {
        const next = { ...(obj || {}) };
        delete next[keyToRemove];
        return next;
    };

    const handleDeleteCustomField = async ({ unitNumber, department, field }) => {
        if (!isAdmin) {
            Toast.error("Only admins can delete custom pricing inputs.");
            return;
        }

        if (!field?.is_custom) return;

        const canDelete = canDeleteCustomField({
            unitNumber,
            department,
            fieldKey: field.key,
        });

        if (!canDelete) {
            Toast.error("Remove pricing before deleting this input.");
            return;
        }

        if (!window.confirm(`Delete custom pricing input "${field.label}"?`)) return;

        try {
            setSavingKey(`delete-custom-${field.key}`);

            const relatedRows = [
                getEntry({ type: "estimation", department, unitNumber }),
                getEntry({ type: "department", department, unitNumber }),
            ].filter(Boolean);

            const updates = relatedRows.map(row =>
                supabase
                    .from("job_pricing_entries")
                    .update({
                        custom_prices: removeKeyFromObject(row.custom_prices, field.key),
                        custom_price_labels: removeKeyFromObject(row.custom_price_labels, field.key),
                        field_meta: removeKeyFromObject(row.field_meta, field.key),
                        updated_by: userProfile.id,
                        updated_by_name: userProfile.full_name || userProfile.username,
                    })
                    .eq("id", row.id)
            );

            const results = await Promise.all(updates);
            const error = results.find(result => result.error)?.error;

            if (error) throw error;

            await logCustomPricingInputDeleted({
                department,
                unitNumber,
                fieldKey: field.key,
                label: field.label,
            });

            Toast.success("Custom pricing input deleted");
            await loadPricing({ silent: true });
            onSaved?.();
        } catch (error) {
            console.error("Failed to delete custom pricing input:", error);
            Toast.error(error.message || "Failed to delete custom pricing input");
        } finally {
            setSavingKey(null);
        }
    };

    const PriceCell = ({ type, department = null, unitNumber, field }) => {
        const entry = getEntry({ type, department, unitNumber });
        const value = getFieldValue(entry, field.key);
        const key = getEditorKey({ type, department, unitNumber, fieldKey: field.key });
        const isEditing = editingKey === key;

        const designerAlreadyPriced =
            type === "estimation" &&
            hasDesignerPriceForEstimatorField({
                unitNumber,
                fieldKey: field.key,
                department: field.department,
            });

        const locked =
            type === "estimation"
                ? !canEditAllPricing || (designerAlreadyPriced && !isAdmin)
                : !canEditDesignerPricing || (value != null && !canEditAllPricing);

        const finalPriceForDepartment =
            type === "department" ? getFinalPriceForDepartment(department) : null;

        const canDeleteThisCustomField =
            isAdmin &&
            field.is_custom &&
            type === "department" &&
            canDeleteCustomField({
                unitNumber,
                department,
                fieldKey: field.key,
            });

        return (
            <div className="relative rounded-xl bg-white border border-gray-100 p-3 pr-20 min-h-[92px]">
                <div>
                    <div className="min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase">
                                {field.label}
                            </p>
                        </div>

                        {isEditing ? (
                            <div className="mt-2 relative">
                                <MoneyInput value={draftValue} onChange={setDraftValue} />

                                {type === "department" && (
                                    <div className="absolute left-0 top-full mt-1 z-50 w-64 rounded-lg border border-zinc-200 bg-white shadow-xl p-2 text-xs">
                                        <p className="font-semibold text-zinc-700">
                                            Final pricing
                                        </p>

                                        {finalPriceForDepartment != null ? (
                                            <p className="text-zinc-600 mt-0.5">
                                                {formatMoney(finalPriceForDepartment)}
                                            </p>
                                        ) : (
                                            <p className="text-amber-700 mt-0.5">
                                                Final pricing has not been entered yet.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mt-1">
                                <p className="text-sm font-semibold text-zinc-800">
                                    {formatMoney(value)}
                                </p>

                                {type === "estimation" && designerAlreadyPriced && (
                                    <p className="text-[10px] text-amber-700 font-semibold mt-1">
                                        Designer pricing already exists
                                    </p>
                                )}
                            </div>
                        )}

                        {entry?.field_meta?.[field.key]?.updated_by_name && (
                            <p className="text-[10px] text-gray-400 mt-1">
                                by {entry.field_meta[field.key].updated_by_name}
                            </p>
                        )}
                    </div>

                    <div className="absolute right-3 top-3 flex items-center justify-end gap-1">
                        {locked ? (
                            <FiLock className="w-3.5 h-3.5 text-gray-400 mt-1" />
                        ) : isEditing ? (
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    disabled={savingKey === key}
                                    onClick={() =>
                                        saveSingleValue({
                                            type,
                                            department,
                                            unitNumber,
                                            fieldKey: field.key,
                                            value: draftValue,
                                        })
                                    }
                                    className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                                >
                                    <FiSave className="w-3.5 h-3.5" />
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditingKey(null);
                                        setDraftValue(null);
                                    }}
                                    className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                                >
                                    <FiX className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                {field.is_custom && type === "department" && (
                                    canDeleteThisCustomField ? (
                                        <button
                                            type="button"
                                            disabled={savingKey === `delete-custom-${field.key}`}
                                            onClick={() =>
                                                handleDeleteCustomField({
                                                    unitNumber,
                                                    department,
                                                    field,
                                                })
                                            }
                                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                                        >
                                            <FiTrash2 className="w-3.5 h-3.5" />
                                        </button>
                                    ) : (
                                        <TextTooltip text={isAdmin ? "Remove estimator and designer pricing before deletion" : "Only admins can delete custom pricing inputs"}>
                                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-300 cursor-not-allowed">
                                                <FiTrash2 className="w-3.5 h-3.5" />
                                            </span>
                                        </TextTooltip>
                                    )
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditingKey(key);
                                        setDraftValue(value);
                                    }}
                                    className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
                                >
                                    <FiEdit3 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const ArchivedPriceCell = ({ entry, field }) => {
        const value = getFieldValue(entry, field.key);

        return (
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 opacity-80">
                <p className="text-[11px] font-semibold text-gray-400 uppercase">
                    {field.label}
                </p>

                <p className="text-sm font-semibold text-zinc-700 mt-1">
                    {formatMoney(value)}
                </p>

                {entry?.field_meta?.[field.key]?.updated_by_name && (
                    <p className="text-[10px] text-gray-400 mt-1">
                        by {entry.field_meta[field.key].updated_by_name}
                    </p>
                )}
            </div>
        );
    };

    const handleUpdateQuote = async () => {
        if (!window.confirm("Archive current pricing and start a new quote revision?")) {
            return;
        }

        try {
            const currentEntries = entries.filter(
                e =>
                    !e.is_archived &&
                    Number(e.quote_revision || 1) === activeRevision
            );

            if (!currentEntries.length) {
                Toast.error("No pricing entries found.");
                return;
            }


            const newRows = currentEntries.map(row => {
                const copy = { ...row };

                delete copy.id;
                delete copy.child_id_key;
                delete copy.department_key;
                delete copy.created_at;
                delete copy.updated_at;

                return {
                    ...copy,
                    quote_revision: activeRevision + 1,
                    is_archived: false,
                    archived_at: null,
                    archived_by: null,
                    archived_by_name: null,
                };
            });

            const currentEntryIds = currentEntries.map(row => row.id);

            const { error: archiveError } = await supabase
                .from("job_pricing_entries")
                .update({
                    is_archived: true,
                    archived_at: new Date().toISOString(),
                    archived_by: userProfile.id,
                    archived_by_name: userProfile.full_name || userProfile.username,
                })
                .in("id", currentEntryIds);

            if (archiveError) throw archiveError;

            const { error: createError } = await supabase
                .from("job_pricing_entries")
                .insert(newRows);

            if (createError) throw createError;

            await logPricingRevisionCreated({
                oldRevision: activeRevision,
                newRevision: activeRevision + 1,
                archivedCount: currentEntries.length,
            });

            Toast.success("Quote revision created.");
            await loadPricing({ silent: true });
            onSaved?.();
        } catch (error) {
            console.error(error);
            Toast.error(error.message);
        }
    };

    const handleAddCustomField = async () => {
        const department = customFieldModal.department;
        const label = customFieldModal.label.trim();
        const unitNumber = customFieldModal.unitNumber;

        if (!department || !label || !unitNumber) {
            Toast.error("Please select department and enter a field name.");
            return;
        }

        const fieldKey = makeCustomFieldKey();
        const userName = userProfile.full_name || userProfile.username;

        try {
            setSavingKey(`custom-field-${unitNumber}`);

            const rowsToUpsert = [
                {
                    type: "estimation",
                    department: null,
                },
                {
                    type: "department",
                    department,
                },
            ].map(({ type, department }) => {
                const existing = getEntry({ type, department, unitNumber });

                return {
                    company_id: company.id,
                    job_id: assignment.id,
                    child_id: null,
                    type,
                    department,
                    unit_number: unitNumber,

                    roof: existing?.roof ?? null,
                    labour: existing?.labour ?? null,
                    first_floor: existing?.first_floor ?? null,
                    second_floor: existing?.second_floor ?? null,
                    gf_wall: existing?.gf_wall ?? null,
                    first_floor_wall: existing?.first_floor_wall ?? null,
                    second_floor_wall: existing?.second_floor_wall ?? null,
                    beams: existing?.beams ?? null,
                    flooring_sheets: existing?.flooring_sheets ?? null,

                    custom_prices: existing?.custom_prices || {},
                    custom_price_labels: {
                        ...(existing?.custom_price_labels || {}),
                        [fieldKey]: label,
                    },

                    created_by: existing?.created_by || userProfile.id,
                    created_by_name: existing?.created_by_name || userName,
                    updated_by: userProfile.id,
                    updated_by_name: userName,
                    field_meta: existing?.field_meta || {},
                    quote_revision: activeRevision,
                    is_archived: false,
                };
            });

            const { error } = await supabase
                .from("job_pricing_entries")
                .upsert(rowsToUpsert, {
                    onConflict: "job_id,type,unit_number,child_id_key,department_key,quote_revision",
                });

            if (error) throw error;

            await logCustomPricingInputCreated({
                department,
                unitNumber,
                fieldKey,
                label,
            });

            await loadPricing({ silent: true });

            setCustomFieldModal({
                open: false,
                unitNumber: null,
                department: "",
                label: "",
            });

            Toast.success("Custom pricing field added");
            onSaved?.();
        } catch (error) {
            console.error("Failed to add custom pricing field:", error);
            Toast.error(error.message || "Failed to add custom pricing field");
        } finally {
            setSavingKey(null);
        }
    };

    const addUnit = () => {
        const nextUnit = unitNumbers.length
            ? Math.max(...unitNumbers) + 1
            : 1;

        setManualUnitNumbers(prev => [...new Set([...prev, nextUnit])]);

        setOpenUnits(prev => ({
            ...prev,
            [nextUnit]: true,
        }));
    };

    const hasSavedEntriesForUnit = (unitNumber) => {
        return entries.some(entry =>
            !entry.is_archived &&
            Number(entry.quote_revision || 1) === activeRevision &&
            Number(entry.unit_number) === Number(unitNumber)
        );
    };

    const removeUnit = async (unitNumber) => {
        const confirmed = window.confirm(
            `Delete Unit ${unitNumber} pricing rows? This will remove estimator and department pricing for this unit in Revision ${activeRevision}.`
        );

        if (!confirmed) return;

        try {
            setSavingKey(`delete-unit-${unitNumber}`);

            const { error } = await supabase
                .from("job_pricing_entries")
                .delete()
                .eq("company_id", company.id)
                .eq("job_id", assignment.id)
                .eq("quote_revision", activeRevision)
                .eq("unit_number", unitNumber)
                .eq("is_archived", false);

            if (error) throw error;

            await supabase.from("job_action_logs").insert({
                job_id: assignment.id,
                company_id: company.id,
                user_id: userProfile.id,
                user_name: userProfile.full_name || userProfile.username,
                user_role: userProfile.role,
                action: "pricing_unit_deleted",
                details: `Pricing Unit ${unitNumber} deleted from Revision ${activeRevision}`,
                changed_fields: ["unit_number"],
                old_values: {
                    unit_number: unitNumber,
                    quote_revision: activeRevision,
                },
                new_values: null,
                action_origin: "pricing_review_dropdown",
                timestamp: new Date().toISOString(),
            });

            setManualUnitNumbers(prev =>
                prev.filter(num => Number(num) !== Number(unitNumber))
            );

            setRemovedUnitNumbers(prev => [...new Set([...prev, Number(unitNumber)])]);

            setOpenUnits(prev => {
                const next = { ...prev };
                delete next[unitNumber];
                return next;
            });

            await loadPricing();
            onSaved?.();

            Toast.success(`Unit ${unitNumber} deleted`);
        } catch (error) {
            console.error("Failed to delete pricing unit:", error);
            Toast.error(error.message || "Failed to delete pricing unit");
        } finally {
            setSavingKey(null);
        }
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-visible">
            <div className="job-pricing-toolbar flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div>
                    <p className="text-sm font-semibold text-zinc-800">Pricing Review</p>
                    <p className="text-xs text-gray-500">Realtime estimator and designer pricing values.</p>
                </div>

                <div className="flex flex-row items-center gap-2">
                    <button
                        type="button"
                        onClick={handleUpdateQuote}
                        className="inline-flex items-center gap-2 rounded-lg bg-zinc-800 px-3 py-2 text-xs font-semibold text-white hover:bg-zinc-700"
                    >
                        New Quote Revision
                    </button>
                    <button
                        type="button"
                        onClick={addUnit}
                        className="inline-flex items-center gap-2 rounded-lg bg-zinc-900 px-3 py-2 text-xs font-semibold text-white hover:bg-zinc-800"
                    >
                        <FiPlus className="w-3.5 h-3.5" />
                        Add Unit
                    </button>
                    <button
                        type="button"
                        onClick={loadPricing}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                    >
                        <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="p-4">
                {allowedQuoteTypes.length > 0 && (() => {
                    const effectivePricing = computeEffectivePricing({ finalPricings, estimationTotals });

                    return (
                    <div className="mb-3 space-y-1.5">
                        {allowedQuoteTypes.map(department => {
                            const info = effectivePricing.departments[department];

                            const amountLabel = info?.priced
                                ? formatMoney(info.amount)
                                : "Not entered";

                            const sent = sentDepartments.has(department);

                            return (
                                <button
                                    key={department}
                                    type="button"
                                    onClick={() => setShowFinalPricingModal(true)}
                                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 text-left"
                                >
                                    <span className="text-xs font-medium text-gray-700 capitalize">{department} final pricing</span>
                                    <span className="flex items-center gap-2 text-xs">
                                        <span className="font-semibold text-gray-800 tabular-nums">
                                            {amountLabel}
                                        </span>
                                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase ${sent ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                                            {sent ? "Sent" : "Not sent"}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    );
                })()}

                {jobIncludesEstimation && !hasEstimatorPricing && (
                    <div className="mb-3 rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-800 font-semibold">
                        Estimator has not entered prices yet. Designers can still enter department pricing.
                    </div>
                )}

                {loading && entries.length === 0 ? (
                    <p className="text-sm font-semibold text-gray-400">Loading pricing...</p>
                ) : unitNumbers.length === 0 ? (
                    <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-800 font-semibold">
                        No pricing units found yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {unitNumbers.map((unitNumber, index) => {
                            const isOpen = openUnits[unitNumber] === true;
                            const estimator = getEntry({ type: "estimation", unitNumber });
                            const headerPriceItems = getUnitHeaderPriceItems(unitNumber);

                            return (
                                <div key={unitNumber} className="rounded-2xl border border-gray-100 bg-gray-50 overflow-visible">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setOpenUnits(prev => ({
                                                ...prev,
                                                [unitNumber]: !prev[unitNumber],
                                            }))
                                        }
                                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/60"
                                    >
                                        <div className="min-w-0 flex-1 flex flex-col items-start">
                                            <p className="text-sm font-semibold text-zinc-800">
                                                Unit {index + 1}
                                            </p>

                                            <div className="mt-0.5 flex flex-col w-full min-w-0 items-start gap-2">
                                                <p className="shrink-0 text-[11px] text-gray-500">
                                                    Revision {activeRevision}
                                                </p>

                                                {!isOpen && headerPriceItems.length > 0 && (
                                                    <div className="flex flex-wrap">
                                                        <div className="flex flex-wrap items-center gap-1.5 whitespace-nowrap">
                                                            {headerPriceItems.map(item => (
                                                                <span
                                                                    key={item.key}
                                                                    className="inline-flex max-w-[170px] shrink-0 items-center gap-1 rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600 shadow-sm"
                                                                    title={`${item.label}: ${formatMoney(item.value)}`}
                                                                >
                                                                    <span className="truncate text-gray-500">
                                                                        {item.label}
                                                                    </span>

                                                                    <span className="text-zinc-800">
                                                                        {formatMoney(item.value)}
                                                                    </span>
                                                                </span>
                                                            ))}

                                                            {/* {headerPriceItems.length > 5 && (
                                                                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-500">
                                                                    +{headerPriceItems.length - 5} more
                                                                </span>
                                                            )} */}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {unitNumbers.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeUnit(unitNumber);
                                                    }}
                                                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-100"
                                                    title="Remove unit"
                                                >
                                                    <FiTrash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}

                                            <FiChevronDown className={`w-4 h-4 text-gray-500 transition ${isOpen ? "rotate-180" : ""}`} />
                                        </div>
                                    </button>

                                    {isOpen && (
                                        <div className="border-t border-white p-4 space-y-4 overflow-visible">
                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setCustomFieldModal({
                                                            open: true,
                                                            unitNumber,
                                                            department: allowedQuoteTypes[0] || "",
                                                            label: "",
                                                        })
                                                    }
                                                    className="inline-flex items-center gap-2 rounded-lg bg-white border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                                                >
                                                    <FiPlus className="w-3.5 h-3.5" />
                                                    Add Custom Pricing Input
                                                </button>
                                            </div>
                                            <div className="rounded-2xl bg-white border border-gray-200 p-3 shadow-sm">
                                                <div className="mb-3 flex items-start justify-between gap-3 border-b border-gray-100 pb-2">
                                                    <div>
                                                        <p className="text-sm font-bold text-zinc-800">
                                                            Estimator Pricing
                                                        </p>

                                                        <p className="text-[11px] text-gray-500 mt-0.5">
                                                            Main quoted pricing entered by the estimator for this unit.
                                                        </p>
                                                    </div>

                                                    <div className="flex shrink-0 flex-col items-end gap-1">
                                                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                                                            Quote
                                                        </span>

                                                        {(() => {
                                                            const estimatorTotal = getEstimatorSectionTotal(unitNumber);
                                                            return (
                                                                <>
                                                                    <p className="text-sm font-bold text-zinc-800 tabular-nums">
                                                                        {estimatorTotal != null ? formatMoney(estimatorTotal) : "Not entered"}
                                                                    </p>
                                                                    <p className="text-[10px] text-gray-400 italic">
                                                                        Labour is excluded
                                                                    </p>
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                    {getEstimatorFieldsForUnit(unitNumber).map(field => (
                                                        <PriceCell
                                                            key={`est-${unitNumber}-${field.department || "general"}-${field.key}`}
                                                            type="estimation"
                                                            department={field.department}
                                                            unitNumber={unitNumber}
                                                            field={field}
                                                        />
                                                    ))}
                                                </div>
                                            </div>

                                            {allowedQuoteTypes.map(quoteType => {
                                                const sectionTotal = getDepartmentSectionTotal(quoteType, unitNumber);
                                                const sectionComparison = getDepartmentSectionComparison(quoteType, unitNumber);
                                                const finalPrice = getFinalPriceForDepartment(quoteType);

                                                return (
                                                <div
                                                    key={`${unitNumber}-${quoteType}`}
                                                    className="rounded-2xl bg-white border border-gray-200 p-3 shadow-sm"
                                                >
                                                    <div className="mb-3 flex items-start justify-between gap-3 border-b border-gray-100 pb-2">
                                                        <div>
                                                            <p className="text-sm font-bold text-zinc-800">
                                                                {getPricingSectionTitle(quoteType)} Pricing
                                                            </p>

                                                            <p className="text-[11px] text-gray-500 mt-0.5">
                                                                {getPricingSectionSubtitle(quoteType)}
                                                            </p>
                                                        </div>

                                                        <div className="flex shrink-0 flex-col items-end gap-1">
                                                            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                                                                {getPricingSectionBadge(quoteType)}
                                                            </span>

                                                            <p className="text-sm font-bold text-zinc-800 tabular-nums">
                                                                {sectionTotal != null ? formatMoney(sectionTotal) : "Not entered"}
                                                            </p>

                                                            {quoteType === "roof" && (
                                                                <p className="text-[10px] text-gray-400 italic">
                                                                    Labour is excluded
                                                                </p>
                                                            )}

                                                            {sectionComparison && (
                                                                <p
                                                                    className={`text-[11px] font-semibold ${sectionComparison.isProfit ? "text-green-700" : "text-red-700"}`}
                                                                    title={`Final pricing: ${formatMoney(finalPrice)}`}
                                                                >
                                                                    {sectionComparison.isProfit ? "+" : "-"}
                                                                    {formatMoney(Math.abs(sectionComparison.diff))} · {Math.abs(sectionComparison.percent).toFixed(1)}%
                                                                    {sectionComparison.isProfit ? " under final" : " over final"}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                        {getFieldsForDepartmentAndUnit(quoteType, unitNumber).map(field => (
                                                            <PriceCell
                                                                key={`${quoteType}-${unitNumber}-${field.key}`}
                                                                type="department"
                                                                department={quoteType}
                                                                unitNumber={unitNumber}
                                                                field={field}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {archivedRevisions.length > 0 && (
                <div className="mt-5 space-y-3 p-4">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">
                        Pricing History
                    </p>

                    {archivedRevisions.map(({ revision, rows }) => {
                        const archivedUnitNumbers = [...new Set(
                            rows.map(e => Number(e.unit_number)).filter(Boolean)
                        )].sort((a, b) => a - b);

                        const archivedAt = rows.find(r => r.archived_at)?.archived_at;
                        const archivedBy = rows.find(r => r.archived_by_name)?.archived_by_name;

                        const subtext = [
                            "Archived",
                            archivedBy ? `by ${archivedBy}` : null,
                            archivedAt ? new Date(archivedAt).toLocaleString() : null,
                        ].filter(Boolean).join(" · ");

                        return (
                            <DropdownSection
                                key={`archived-revision-${revision}`}
                                title={`Revision ${revision}`}
                                subtext={subtext}
                                defaultOpen={false}
                                customMainStyle="!bg-gray-50"
                                customClickableStyle="!px-4 !py-3"
                                customContainerStyle="!p-4 !bg-gray-50"
                                titleStyle="text-sm"
                                subtextStyle="text-[11px] text-gray-500"
                            >
                                <div className="space-y-3">
                                    {archivedUnitNumbers.map((unitNumber, index) => {
                                        const estimator = getArchivedEntry({
                                            rows,
                                            type: "estimation",
                                            unitNumber,
                                        });

                                        return (
                                            <DropdownSection
                                                key={`archived-${revision}-${unitNumber}`}
                                                title={`Unit ${index + 1}`}
                                                subtext="Archived pricing"
                                                defaultOpen={false}
                                                customMainStyle="!shadow-none !border-gray-200"
                                                customClickableStyle="!px-4 !py-3"
                                                customContainerStyle="!p-4 !bg-white"
                                                titleStyle="text-sm"
                                                subtextStyle="text-[11px] text-gray-400"
                                            >
                                                <div className="space-y-4">
                                                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                                                        <div className="mb-3 flex items-start justify-between gap-3 border-b border-gray-200 pb-2">
                                                            <div>
                                                                <p className="text-sm font-bold text-zinc-700">
                                                                    Estimator Pricing
                                                                </p>

                                                                <p className="text-[11px] text-gray-500 mt-0.5">
                                                                    Archived estimator pricing for this unit.
                                                                </p>
                                                            </div>

                                                            <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500 border border-gray-200">
                                                                Quote
                                                            </span>
                                                        </div>

                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                            {getEstimatorFieldsForUnit(unitNumber).map(field => (
                                                                <ArchivedPriceCell
                                                                    key={`arch-est-${revision}-${unitNumber}-${field.key}`}
                                                                    entry={estimator}
                                                                    field={field}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {allowedQuoteTypes.map(quoteType => {
                                                        const departmentEntry = getArchivedEntry({
                                                            rows,
                                                            type: "department",
                                                            department: quoteType,
                                                            unitNumber,
                                                        });

                                                        return (
                                                            <div
                                                                key={`arch-${revision}-${unitNumber}-${quoteType}`}
                                                                className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                                                            >
                                                                <div className="mb-3 flex items-start justify-between gap-3 border-b border-gray-200 pb-2">
                                                                    <div>
                                                                        <p className="text-sm font-bold text-zinc-700">
                                                                            {getPricingSectionTitle(quoteType)} Pricing
                                                                        </p>

                                                                        <p className="text-[11px] text-gray-500 mt-0.5">
                                                                            Archived {getPricingSectionTitle(quoteType).toLowerCase()} pricing.
                                                                        </p>
                                                                    </div>

                                                                    <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-gray-500 border border-gray-200">
                                                                        {getPricingSectionBadge(quoteType)}
                                                                    </span>
                                                                </div>

                                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                    {getFieldsForDepartmentAndUnit(quoteType, unitNumber).map(field => (
                                                                        <ArchivedPriceCell
                                                                            key={`arch-${quoteType}-${revision}-${unitNumber}-${field.key}`}
                                                                            entry={departmentEntry}
                                                                            field={field}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </DropdownSection>
                                        );
                                    })}
                                </div>
                            </DropdownSection>
                        );
                    })}
                </div>
            )}

            {customFieldModal.open && (
                <div className="mobile-dialog-shell fixed inset-0 z-[9999] bg-black/30 flex items-center justify-center p-4">
                    <div className="w-full max-w-md rounded-2xl bg-white border border-gray-200 shadow-xl p-4">
                        <h3 className="text-sm font-semibold text-zinc-900">
                            Add Custom Pricing Input
                        </h3>

                        <p className="text-xs text-gray-500 mt-1">
                            This will create the same input for estimator and designer pricing.
                        </p>

                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Department
                                </label>

                                <select
                                    value={customFieldModal.department}
                                    onChange={(e) =>
                                        setCustomFieldModal(prev => ({
                                            ...prev,
                                            department: e.target.value,
                                        }))
                                    }
                                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                                >
                                    {allowedQuoteTypes.map(type => (
                                        <option key={type} value={type}>
                                            {getPricingSectionTitle(type)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="text-xs font-semibold text-gray-500">
                                    Input Name
                                </label>

                                <input
                                    type="text"
                                    value={customFieldModal.label}
                                    onChange={(e) =>
                                        setCustomFieldModal(prev => ({
                                            ...prev,
                                            label: e.target.value,
                                        }))
                                    }
                                    placeholder="Example: Crane Hire"
                                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                                />
                            </div>
                        </div>

                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() =>
                                    setCustomFieldModal({
                                        open: false,
                                        unitNumber: null,
                                        department: "",
                                        label: "",
                                    })
                                }
                                className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                disabled={savingKey?.startsWith("custom-field")}
                                onClick={handleAddCustomField}
                                className="px-3 py-2 rounded-lg bg-zinc-900 text-xs font-semibold text-white hover:bg-zinc-800 disabled:opacity-50"
                            >
                                Add Input
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showFinalPricingModal && (
                <FinalPricingDetails
                    modalJobId={assignment.id}
                    modalCompanySlug={companySlug}
                    onClose={() => setShowFinalPricingModal(false)}
                    onSaved={({ department, finalPricing }) => {
                        if (!department) return;

                        setFinalPricings(prev => ({
                            ...(prev || {}),
                            [department]: finalPricing?.final_price,
                        }));
                    }}
                />
            )}

        </div>
    );
};

export default PricingReviewDropdown;